import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import type { Glyph } from './svg-glyph';

/**
 * JS bridge for the native `MuckaGlassBarPlugin`
 * (ios/App/App/MuckaGlassBarPlugin.swift +
 *  android/app/src/main/java/ai/mucka/MuckaGlassBarPlugin.kt).
 *
 * Liquid Glass PR 4 (docs/liquid-glass-native-bar-plan.md): the bottom tab row,
 * the Ask Mucka orb and the composer row drawn natively over the WebView, so
 * on iOS 26 they are real `UIGlassEffect` glass over the page beneath.
 *
 * 🚨 The WEB owns every state and every rule. Which tabs a seat gets, which one
 * is current, what the field's placeholder says, what the mic button means
 * right now (talk, send, stop, cancel the wake word) — MobileNav decides all of
 * it and tells native. Native draws what it is told and reports what was
 * tapped and typed. So the voice, wake-word and offline rules are never
 * written twice, and a fix to one of them is a web deploy, not a store release.
 *
 * Only reached through `useUiGlassNative()`, which already requires the plugin
 * to exist in this binary — callers never need their own guard.
 */

/**
 * Icons travel as vector outlines (absolute M/L/C/Z on a 24 box) read from
 * the web's own rendered lucide icons, so the native bar draws exactly the
 * icon the web would — including whatever page icon the fold circle shows.
 * See ./svg-glyph.ts.
 */
export type { Glyph };

export interface GlassBarTab {
  key: string;
  label: string;
  icon: Glyph;
}

/** What the single round button at the end of the composer means right now. */
export type GlassBarMic = 'mic' | 'send' | 'connecting' | 'stop' | 'cancel';

export type GlassBarMode = 'tabs' | 'composer' | 'hidden';

export interface GlassBarConfig {
  /** Left of the orb, in order. */
  left: GlassBarTab[];
  /** Right of the orb, in order. A `null` keeps an empty column so the orb stays centred. */
  right: (GlassBarTab | null)[];
  /** Whether the Ask Mucka orb is there at all (a subcontractor has none). */
  ask: boolean;
  active: string | null;
  dark: boolean;
  /** The composer's fixed glyphs, sent once rather than on every state change. */
  glyphs: Record<'ask' | 'plus' | 'x' | 'mic' | 'send' | 'stop' | 'loader', Glyph>;
}

export interface GlassBarComposer {
  placeholder: string;
  mic: GlassBarMic;
  attachOpen: boolean;
  /** A dropped call blurs the field; the web says so, native fades it. */
  dimmed: boolean;
  /** The circle the tabs fold into while the composer is open. */
  foldIcon: Glyph;
  /**
   * Whether that circle shows. False while the whole conversation is on screen:
   * its header X already puts Mucka away, so the circle was a second button
   * for the same thing and the field gets its room (Tom, 2026-10-10). A build
   * that predates this field ignores it and keeps the circle.
   */
  fold: boolean;
  /**
   * The dark chat card is right behind the composer (the full conversation is
   * open and Light chat is off). iOS 26 glass takes its shade from what is
   * behind it, so there it goes dark whatever we pin; native draws it dark on
   * purpose, with light ink, on both phones (TKT-1619). A build that predates
   * this field ignores it.
   */
  onDark: boolean;
}

export interface GlassBarLayout {
  /** The bar's own height above the bottom edge, safe area included, in CSS px. */
  barHeight: number;
  /** The keyboard's height in CSS px, 0 when it is down. */
  keyboardHeight: number;
}

interface MuckaGlassBarPlugin {
  configure(config: GlassBarConfig): Promise<void>;
  setActive(options: { key: string | null }): Promise<void>;
  setMode(options: { mode: GlassBarMode; animated?: boolean }): Promise<void>;
  setComposer(composer: GlassBarComposer): Promise<void>;
  setDraft(options: { text: string }): Promise<void>;
  focus(): Promise<void>;
  blur(): Promise<void>;
  /** Removes the bar entirely, e.g. when the account leaves the trial. */
  teardown(): Promise<void>;
  /** iOS: the system swipe in from the left edge to go Back. Android: no-op. */
  setBackSwipe(options: { enabled: boolean }): Promise<void>;
  addListener(eventName: 'tab', fn: (e: { key: string }) => void): Promise<PluginListenerHandle>;
  addListener(eventName: 'ask' | 'fold' | 'attach' | 'mic' | 'focus' | 'blur', fn: () => void): Promise<PluginListenerHandle>;
  addListener(eventName: 'submit' | 'draft', fn: (e: { text: string }) => void): Promise<PluginListenerHandle>;
  addListener(eventName: 'layout', fn: (e: GlassBarLayout) => void): Promise<PluginListenerHandle>;
}

export const GlassBar = registerPlugin<MuckaGlassBarPlugin>('MuckaGlassBar');
